import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getProspect, updateProspect } from "../../interfaces/prospect";
import { getEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate } from "../../interfaces/emailTemplate";
import { getMyInfo, setLastUsedTemplate } from "../../interfaces/user";
import { generateEmailComposeUrl, EmailProvider } from "../../utils/emailProviders";
import SendScheduleModal from "../../components/SendScheduleModal";
import TemplatesModal from "../../components/TemplatesModal";

type EmailModel = {
  _id: string;
  name?: string;
  description?: string;
  html?: string;
  text?: string;
  type?: string;
  replyRate?: number;
  lang?: "en" | "fr";
};

const Token = ({ label, onInsert }: { label: string; onInsert: (t: string) => void }) => (
  <button
    type="button"
    className="text-xs text-ink-muted hover:text-ink cursor-pointer transition-colors duration-150"
    onClick={() => onInsert(label)}
    title={`Insert ${label}`}
  >
    {label.replace("{", "").replace("}", "")}
  </button>
);


const defaultFreelanceBodyEN = `Hi {firstName},

I'm a freelance {title} who helps teams like {company} ship faster with less risk.

I had a quick idea for {company} you could test in ~20 minutes:

• Replace the most time-consuming step with a lightweight checklist/automation
• Measure the impact on response rate or time-to-close this week

If this is useful, I can record a 3‑min Loom tailored to {company} showing exactly how I'd do it.

Open to a quick 10‑min chat next week?

Best,`;

export default function ComposeEmail() {
  const params = useParams<{ id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguage] = useState<"en" | "fr">("en");
  const [loading, setLoading] = useState(false);
  const [prospect, setProspect] = useState({
    name: "",
    company: "",
    title: "",
    email: ""
  });
  const [templates, setTemplates] = useState<EmailModel[]>([]);
  // Modal toggle for templates popup
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("Quick idea for {company}");
  const [body, setBody] = useState(defaultFreelanceBodyEN);
  const [showPreview, setShowPreview] = useState(false);
  const [focused, setFocused] = useState<"subject" | "body" | null>(null);
  const [showConfirmSend, setShowConfirmSend] = useState(false);
  const [showPostSendActions, setShowPostSendActions] = useState(false);
  const [emailProvider, setEmailProvider] = useState<EmailProvider>("gmail");

  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const applyTemplate = (tpl: string) => {
    const f: any = prospect || {};
    const name = `${(f.name || "").trim()}`.trim();
    const firstName = `${(f.firstName || "").trim()}`.trim();
    const lastName = `${(f.lastName || "").trim()}`.trim();
    const company = (f.company || "").trim();
    const title = (f.title || "").trim();
    return (tpl || "")
      .replaceAll("{name}", name || "there")
      .replaceAll("{firstName}", firstName || "there")
      .replaceAll("{lastName}", lastName || "there")
      .replaceAll("{company}", company)
      .replaceAll("{title}", title);
  };

  const insertAtCursor = (el: HTMLInputElement | HTMLTextAreaElement | null, text: string) => {
    if (!el) return;
    const start = (el as any).selectionStart || 0;
    const end = (el as any).selectionEnd || 0;
    const value = (el as any).value || "";
    const newValue = value.slice(0, start) + text + value.slice(end);
    (el as any).value = newValue;
    if (el === subjectRef.current) setSubject(newValue);
    if (el === bodyRef.current) setBody(newValue);
    const pos = start + text.length;
    requestAnimationFrame(() => {
      (el as any).setSelectionRange(pos, pos);
      el.focus();
    });
  };

  const onInsertToken = (token: string) => {
    const placeholder = token;
    if (focused === "subject") insertAtCursor(subjectRef.current, placeholder);
    else insertAtCursor(bodyRef.current, placeholder);
  };

  // Derive prospect id from route param or query string (?id or ?prospectId)
  const routeProspectId = useMemo(() => {
    const qs = new URLSearchParams(location.search || "");
    return (params.id || qs.get("id") || qs.get("prospectId") || "").trim();
  }, [params.id, location.search]);

  // Fetch backend templates once using emailTemplate interface
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list: any[] = await getEmailTemplates();
        if (cancelled) return;
        if (Array.isArray(list)) setTemplates(list as any);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load user's last used template and apply by default
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getMyInfo();
        
        // Load email provider preference
        if (data && (data as any).emailProvider) {
          setEmailProvider((data as any).emailProvider);
        }
        
        const lastId = (data && (data as any).lastUsedTemplate) as string | undefined;
        if (!lastId) return;
        // wait until templates fetched
        // poll up to ~1s for templates to be available
        let tries = 0;
        while (!cancelled && tries < 10 && templates.length === 0) {
          await new Promise((r) => setTimeout(r, 100));
          tries++;
        }
        if (cancelled) return;
        const found = templates.find((t) => t._id === lastId);
        if (found) applyFromTemplate(found);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  // Fetch prospect by id and set the To email
  useEffect(() => {
    let cancelled = false;
    if (!routeProspectId) return;
    setLoading(true);
    (async () => {
      try {
        const data: any = await getProspect(routeProspectId);
        if (cancelled) return;
        if (data) {
          setProspect((prev: any) => ({ ...prev, ...data }));
          if (data.email) setToEmail(data.email);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeProspectId]);

  const applyFromTemplate = (t: EmailModel) => {
    const subj = t.name || "";
    const content = applyTemplate(t.html || t.text || "");
    if (subj) setSubject(subj);
    if (content) setBody(content);
    setShowPreview(!!t.html);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(applyTemplate(body));
      alert("Body copied to clipboard");
    } catch {}
  };

  const getEmailComposeUrl = () => {
    return generateEmailComposeUrl(emailProvider, {
      to: toEmail || prospect.email || "",
      subject: applyTemplate(subject),
      body: applyTemplate(body),
    });
  };

  const logSendEmail = async (opts?: { scheduledFor?: string }) => {
    if (!routeProspectId) return;
    try {
      const prevLogs = Array.isArray((prospect as any)?.logs)
        ? ([...(prospect as any).logs] as any[])
        : ([] as any[]);
      const log: any = {
        type: "send-email",
        subject: applyTemplate(subject),
        // Keep message compact to avoid bloating logs
        message: undefined,
        date: opts?.scheduledFor || new Date().toISOString(),
      };
      const nextLogs = [...prevLogs, log];
      setProspect((p: any) => ({ ...p, logs: nextLogs }));
      await updateProspect(routeProspectId, { logs: nextLogs } as any);
    } catch {
      // Silent fail with soft notice
      console.warn("Failed to log send-email event");
    }
  };

  const onClickSend = () => {
    setShowConfirmSend(true);
  };

  const handleSendNow = async () => {
    setShowConfirmSend(false);
    await logSendEmail();
    window.open(getEmailComposeUrl(), "_blank");
    setShowPostSendActions(true);
    setShowConfirmSend(true);
  };

  const handleSchedule = async (scheduleDate: string) => {
    setShowConfirmSend(false);
    try {
      const iso = new Date(scheduleDate).toISOString();
      await logSendEmail({ scheduledFor: iso });
      window.open(getEmailComposeUrl(), "_blank");
      setShowPostSendActions(true);
      setShowConfirmSend(true);
    } catch {}
  };

  const handleGoToKanban = () => {
    navigate("/kanban");
  };

  const handleGoToKanbanAndMoveToContacted = async () => {
    if (!routeProspectId) {
      navigate("/kanban");
      return;
    }

    try {
      await updateProspect(routeProspectId, { status: "contacted" } as any);
      navigate("/kanban");
    } catch (error) {
      console.warn("Failed to update prospect status:", error);
      navigate("/kanban");
    }
  };

  const handleCreateTemplate = async (template: { name: string; description?: string; text: string, type: string }) => {
    const payload: any = {
      name: template.name.trim(),
      description: template.description?.trim() || undefined,
      text: template.text,
      type: template.type,
      lang: language,
    };
    const created = await createEmailTemplate(payload);
    setTemplates((prev) => [created, ...prev]);
  };

  const handleUpdateTemplate = async (id: string, updates: any) => {
    const updated = await updateEmailTemplate(id, updates);
    setTemplates((prev) => prev.map((x) => (x._id === id ? updated : x)));
  };

  const handleDeleteTemplate = async (id: string) => {
    await deleteEmailTemplate(id);
    setTemplates((prev) => prev.filter((x) => x._id !== id));
  };

  const handleSetLastUsedTemplate = async (id: string) => {
    try {
      await setLastUsedTemplate(id);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-canvas font-sans">
      {/* Header */}
      <div className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button className="text-ink-muted hover:text-ink text-xs transition-colors duration-150" onClick={() => navigate(-1)}>← Back</button>
              <h1 className="flex gap-2 text-sm text-ink">Email to {toEmail}</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-3 h-8 text-sm border border-transparent rounded-lg bg-ink text-ink-inverse hover:bg-ink/90 transition-colors duration-150"
                onClick={() => setShowTemplatesModal(true)}
              >
                Templates
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-5">
        <div className="grid grid-cols-12 gap-8">
          {/* Main Compose Area - Flat Design */}
          <div className="col-span-12 lg:col-span-12">
            <div className="bg-surface rounded-2xl border border-border p-4">

              {/* Subject Field */}
              <div className="py-4">
                <div className="flex items-center">
                  <div className="w-20 text-xs text-ink">Subject</div>
                  <input
                    ref={subjectRef}
                    onFocus={() => setFocused("subject")}
                    className="flex-1 bg-transparent text-xs text-ink placeholder-ink-faint focus:outline-none border-none"
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>
              </div>

              {/* Separator Line */}
              <div className="border-t border-border"></div>

              {/* Variables Section */}
              <div className="py-3">
                <div className="flex items-center">
                  <div className="text-xs text-ink-muted">Variables</div>
                  <div className="ml-6 flex gap-6">
                    <Token label="{firstName}" onInsert={onInsertToken} />
                    <Token label="{lastName}" onInsert={onInsertToken} />
                    <Token label="{company}" onInsert={onInsertToken} />
                    <Token label="{title}" onInsert={onInsertToken} />
                  </div>
                </div>
              </div>
              {/* Email Body */}
              <div className="pt-3">
                {!showPreview ? (
                  <textarea
                    ref={bodyRef}
                    onFocus={() => setFocused("body")}
                    className="w-full h-[360px] p-3 bg-transparent border border-border rounded-lg text-[13px] text-ink placeholder-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent resize-none"
                    placeholder="Compose your email..."
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                  />
                ) : (
                  <div className="p-3 rounded-xl h-[290px] overflow-auto text-[13px] text-ink bg-surface-sunken cursor-not-allowed">
                    <div
                      className="whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: applyTemplate(body).replace(/\n/g, '<br>') }}
                    />
                  </div>
                )}
              </div>

              {/* Bottom Actions */}
              <div className="ml-5 flex items-center justify-between">
                <div className="flex items-center">
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className={`text-xs text-ink hover:text-ink-muted transition-colors duration-150 ${showPreview ? 'font-semibold' : 'font-normal'}`}
                  >
                    {showPreview ? 'Edit' : 'Preview'}
                  </button>
                </div>

                <div className="px-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button className="text-xs text-ink-muted hover:text-ink font-medium transition-colors duration-150">{"{}"}</button>
                    <button className="text-xs text-ink-muted hover:text-ink font-bold transition-colors duration-150">B</button>
                    <button className="text-xs text-ink-muted hover:text-ink italic transition-colors duration-150">I</button>
                    <button className="text-xs text-ink-muted hover:text-ink underline transition-colors duration-150">U</button>
                    <div className="w-px h-4 bg-border" />
                  </div>

                  <div className="ml-3 flex items-center gap-3">
                    <button
                      onClick={copyToClipboard}
                      disabled={loading}
                      className="text-xs text-ink-muted hover:text-ink disabled:opacity-50 transition-colors duration-150"
                    >
                      Copy
                    </button>
                    <button
                      className="px-4 py-2 rounded-lg bg-ink text-ink-inverse text-xs hover:bg-ink/90 disabled:opacity-50 transition-colors duration-150"
                      disabled={loading}
                      onClick={onClickSend}
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Templates Modal */}
      <TemplatesModal
        isOpen={showTemplatesModal}
        onClose={() => setShowTemplatesModal(false)}
        onSelectTemplate={applyFromTemplate}
        templates={templates}
        language={language}
        onLanguageChange={setLanguage}
        onCreateTemplate={handleCreateTemplate}
        onUpdateTemplate={handleUpdateTemplate}
        onDeleteTemplate={handleDeleteTemplate}
        onSetLastUsedTemplate={handleSetLastUsedTemplate}
        applyTemplate={applyTemplate}
      />

      {/* Send/Schedule Modal */}
      <SendScheduleModal
        isOpen={showConfirmSend}
        onClose={() => {
          setShowConfirmSend(false);
          setShowPostSendActions(false);
        }}
        onSendNow={handleSendNow}
        onSchedule={handleSchedule}
        onGoToKanban={handleGoToKanban}
        onGoToKanbanAndMoveToContacted={handleGoToKanbanAndMoveToContacted}
        showPostSendActions={showPostSendActions}
      />
    </div>
  );
}
